import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  ClipboardList,
  FileText,
  Settings,
  Calendar,
  HelpCircle,
  BarChart3,
  Palmtree,
  MessageSquare,
  UsersRound,
  Keyboard,
} from "lucide-react";
import { useKeyboardShortcuts, getShortcutsList } from "@/hooks/useKeyboardShortcuts";

interface CommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  keywords?: string[];
  group: "navigation" | "actions";
}

const navigationItems: CommandItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: <LayoutDashboard className="h-4 w-4" />,
    path: "/dashboard",
    keywords: ["home", "overview"],
    group: "navigation",
  },
  {
    id: "my-reviews",
    label: "My Reviews",
    icon: <ClipboardList className="h-4 w-4" />,
    path: "/my-reviews",
    keywords: ["self", "evaluation"],
    group: "navigation",
  },
  {
    id: "my-profile",
    label: "My Profile",
    icon: <UserCircle className="h-4 w-4" />,
    path: "/my-profile",
    keywords: ["account", "settings"],
    group: "navigation",
  },
  {
    id: "teams",
    label: "Teams",
    icon: <UsersRound className="h-4 w-4" />,
    path: "/teams",
    keywords: ["groups", "departments"],
    group: "navigation",
  },
  {
    id: "staff-members",
    label: "Staff Members",
    icon: <Users className="h-4 w-4" />,
    path: "/staff-members",
    keywords: ["employees", "people"],
    group: "navigation",
  },
  {
    id: "templates",
    label: "Templates",
    icon: <FileText className="h-4 w-4" />,
    path: "/templates",
    keywords: ["forms", "questionnaires"],
    group: "navigation",
  },
  {
    id: "review-cycles",
    label: "Review Cycles",
    icon: <ClipboardList className="h-4 w-4" />,
    path: "/cycles",
    keywords: ["evaluations", "performance"],
    group: "navigation",
  },
  {
    id: "analytics",
    label: "Analytics",
    icon: <BarChart3 className="h-4 w-4" />,
    path: "/analytics",
    keywords: ["reports", "metrics", "data"],
    group: "navigation",
  },
  {
    id: "calendar",
    label: "Team Calendar",
    icon: <Calendar className="h-4 w-4" />,
    path: "/calendar",
    keywords: ["schedule", "time off"],
    group: "navigation",
  },
  {
    id: "leave-policies",
    label: "Leave Policies",
    icon: <Palmtree className="h-4 w-4" />,
    path: "/leave-policies",
    keywords: ["vacation", "pto", "time off"],
    group: "navigation",
  },
  {
    id: "surveys",
    label: "My Surveys",
    icon: <MessageSquare className="h-4 w-4" />,
    path: "/my-surveys",
    keywords: ["feedback", "polls"],
    group: "navigation",
  },
  {
    id: "settings",
    label: "Settings",
    icon: <Settings className="h-4 w-4" />,
    path: "/settings",
    keywords: ["preferences", "configuration"],
    group: "navigation",
  },
  {
    id: "help",
    label: "Help Center",
    icon: <HelpCircle className="h-4 w-4" />,
    path: "/help-center",
    keywords: ["support", "documentation", "faq"],
    group: "navigation",
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const navigate = useNavigate();

  // Enable keyboard shortcuts globally
  useKeyboardShortcuts();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      // Show shortcuts help with ?
      if (e.key === "?" && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
          e.preventDefault();
          setShowShortcuts((s) => !s);
        }
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate]
  );

  const shortcuts = getShortcutsList();

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigation">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.keywords?.join(" ") || ""}`}
                onSelect={() => handleSelect(item.path)}
                className="cursor-pointer"
              >
                {item.icon}
                <span className="ml-2">{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Help">
            <CommandItem
              value="keyboard shortcuts"
              onSelect={() => {
                setOpen(false);
                setShowShortcuts(true);
              }}
              className="cursor-pointer"
            >
              <Keyboard className="h-4 w-4" />
              <span className="ml-2">Keyboard Shortcuts</span>
              <kbd className="ml-auto text-xs text-muted-foreground">?</kbd>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      {/* Keyboard shortcuts dialog */}
      <CommandDialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">Keyboard Shortcuts</h2>
          <div className="space-y-2">
            {shortcuts.map((shortcut) => (
              <div
                key={shortcut.key}
                className="flex items-center justify-between py-2 border-b border-border last:border-0"
              >
                <span className="text-sm text-muted-foreground">
                  {shortcut.description}
                </span>
                <kbd className="px-2 py-1 text-xs font-mono bg-muted rounded border">
                  {shortcut.key}
                </kbd>
              </div>
            ))}
          </div>
        </div>
      </CommandDialog>
    </>
  );
}
